import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { yupResolver } from '@hookform/resolvers/yup';
import { unwrapResult } from '@reduxjs/toolkit';
import { Country, State } from 'country-state-city';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { object, string } from 'yup';
import { BillingInformation } from '../../../models';
import {
  fromAccounts,
  fromAuth,
  fromBillingInformation,
  useAppDispatch,
} from '../../../store';
import { track } from '../../../analytics';
import { CommonAutocomplete } from '../../common/CommonAutocomplete';

const useStyles = makeStyles(() => ({
  // Design 6.10: a wide left column (Email · Address line · State) next to a narrow right one
  // (Country · City · Postal code).
  grid: {
    marginTop: 12,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
    gap: '12px 16px',
  },
  fullRow: {
    gridColumn: '1 / -1',
    minWidth: 0,
  },
  field: {
    minWidth: 0,
  },
  label: {
    display: 'block',
    marginBottom: 6,
    fontFamily: 'Inter, -apple-system, system-ui, sans-serif',
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
    lineHeight: 1.3,
  },
  required: {
    color: '#d14343',
  },
  input: {
    width: '100%',
    height: 36,
    boxSizing: 'border-box',
    padding: '0 14px',
    border: '1px solid #e1e7e4',
    borderRadius: 10,
    backgroundColor: '#fff',
    fontSize: 14,
    color: '#16141d',
    fontFamily: 'inherit',
    transition: 'border-color .15s ease, box-shadow .15s ease',
    '&.Mui-focused': {
      borderColor: '#0f8461',
      boxShadow: '0 0 0 3px rgba(15,132,97,.15)',
    },
    '& input': {
      padding: 0,
      height: 'auto',
      '&::placeholder': {
        color: '#7f8b86',
        opacity: 1,
      },
    },
  },
  inputError: {
    borderColor: '#d14343',
    '&.Mui-focused': {
      borderColor: '#d14343',
      boxShadow: '0 0 0 3px rgba(209,67,67,.15)',
    },
  },
  // CommonAutocomplete (MUI Autocomplete) restyled to the 44px / 10px-radius input of the design.
  select: {
    '& .MuiOutlinedInput-root': {
      height: 36,
      minHeight: 36,
      padding: '0 36px 0 14px',
      borderRadius: 10,
      fontSize: 14,
      color: '#16141d',
      backgroundColor: '#fff',
    },
    '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
      padding: 0,
      '&::placeholder': {
        color: '#7f8b86',
        opacity: 1,
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e1e7e4',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#cdd6d2',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0f8461',
      borderWidth: 1,
    },
    '& .MuiOutlinedInput-root.Mui-focused': {
      boxShadow: '0 0 0 3px rgba(15,132,97,.15)',
    },
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d14343',
    },
    '& .MuiFormHelperText-root': {
      margin: '6px 0 0',
      fontSize: 12,
      color: '#d14343',
    },
    '& .MuiAutocomplete-endAdornment': {
      right: 12,
    },
  },
  errorText: {
    marginTop: 6,
    fontSize: 12,
    lineHeight: 1.4,
    color: '#d14343',
  },
  companyToggle: {
    alignSelf: 'flex-start',
    marginTop: 4,
    minHeight: 44,
    padding: 0,
    border: 'none',
    background: 'none',
    font: 'inherit',
    color: '#0b6b4f',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  companyPanel: {
    marginTop: 4,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
    gap: '12px 16px',
    padding: 16,
    backgroundColor: '#f3f5f4',
    borderRadius: 12,
  },
  editNote: {
    margin: '4px 0 0',
    fontSize: 12,
    color: '#5b6661',
  },
  summary: {
    margin: '12px 0 0',
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: '12px 16px',
    padding: 16,
    border: '1px solid #e1e7e4',
    borderRadius: 12,
    backgroundColor: '#fff',
  },
  summaryTerm: {
    fontSize: 12,
    color: '#5b6661',
  },
  summaryValue: {
    margin: '2px 0 0',
    fontSize: 14,
    color: '#16141d',
    overflowWrap: 'anywhere',
  },
}));

export interface BillingSaveOutcome {
  /** Checkout may proceed (form valid and persisted, or nothing to persist). */
  ok: boolean;
  /** The address was (re)written on the BE — the order preview must be re-priced (tax). */
  saved: boolean;
  /**
   * The address just written when `saved`. A caller that already captured the store's billing
   * record (e.g. a click handler's closure) must use this one: its own copy predates the save.
   */
  billingInformation?: Partial<BillingInformation>;
}

/**
 * Error code CE returns when the tax service cannot resolve the address (e.g. a US ZIP outside the
 * selected state): the save is rejected and the form flags the postal code (#1282).
 */
export const BILLING_ADDRESS_TAX_UNRESOLVABLE = 'billing.information.address.tax.unresolvable';

export interface BillingDetailsFormHandle {
  /**
   * Validates the form and persists it when something changed (or the stored address is out of
   * sync with Recurly). In summary mode (saved address, not editing) it resolves `ok` at once.
   */
  validateAndSave: () => Promise<BillingSaveOutcome>;
  /** The customer changed something in the open form since it was opened / last reset. */
  isDirty: () => boolean;
  /** Drops the edits: the form mirrors the stored address again. */
  discard: () => void;
}

interface BillingDetailsFormProps {
  accountId: number;
  /** Saved address shown as a read-only summary (the section's "Edit" opens the form). */
  summary: boolean;
  /** Form opened on a saved address: shows the "Changes apply to this order…" note. */
  editingSaved: boolean;
}

interface BillingFormValues {
  ccEmail: string;
  country: string;
  address1: string;
  city: string;
  state: string;
  postalCode: string;
  fullBusinessName: string;
  vatNumber: string;
}

type EditableKey = keyof BillingFormValues;
const EDITABLE_KEYS: EditableKey[] = [
  'ccEmail', 'country', 'address1', 'city', 'state', 'postalCode', 'fullBusinessName', 'vatNumber',
];

const toFormValues = (
  billing: BillingInformation | undefined,
  defaultEmail: string,
): BillingFormValues => ({
  ccEmail: billing?.ccEmail || defaultEmail,
  country: billing?.country ?? '',
  address1: billing?.address1 ?? '',
  city: billing?.city ?? '',
  state: billing?.state ?? '',
  postalCode: billing?.postalCode ?? '',
  fullBusinessName: billing?.fullBusinessName ?? '',
  vatNumber: billing?.vatNumber ?? '',
});

/** The form's rules; `message` turns a message id into the text shown. */
const billingSchema = (message: (id: string) => string) => object().shape({
  ccEmail: string().trim()
    .required(message('page.order.confirmation.v2.billing.email.error'))
    .email(message('page.order.confirmation.v2.billing.email.invalid')),
  country: string().trim()
    .required(message('page.order.confirmation.v2.billing.country.error')),
  address1: string().trim()
    .required(message('page.order.confirmation.v2.billing.address.error')),
  city: string().trim()
    .required(message('page.order.confirmation.v2.billing.city.error'))
    .min(2, message('page.order.confirmation.v2.billing.city.short'))
    .matches(/^\D*$/, message('page.order.confirmation.v2.billing.city.invalid')),
  state: string().trim()
    .required(message('page.order.confirmation.v2.billing.state.error')),
  postalCode: string().trim()
    .required(message('page.order.confirmation.v2.billing.postal.error'))
    .matches(/^[a-zA-Z0-9 -]*$/, message('page.order.confirmation.v2.billing.postal.invalid')),
  fullBusinessName: string(),
  vatNumber: string()
    .matches(/^[a-zA-Z0-9 .-]*$/, message('page.order.confirmation.v2.billing.company.tax.invalid')),
});

/**
 * A saved address passes the form's own rules (the signed-in email stands in for a missing
 * one, as the form prefills it). One that does not — e.g. saved before City was collected —
 * is not "on file": checkout opens the form so Pay now validates it (Aikido).
 */
export const isBillingInformationComplete = (
  billing: BillingInformation | undefined,
  defaultEmail = '',
): boolean => !!billing && billingSchema(id => id).isValidSync(toFormValues(billing, defaultEmail));

const countryName = (isoCode: string) => Country.getCountryByCode(isoCode)?.name ?? isoCode;
// A saved state may be an ISO code ("CA"): show its name, as the Payment & billing page does.
const stateName = (countryCode: string, state: string) => State.getStatesOfCountry(countryCode)
  .find(it => it.isoCode === state || it.name === state)?.name ?? state;

/**
 * Billing information block of the Payment & Billing page (design 6.10): Email · Country ·
 * Address line · City · State/Province · Postal code, plus an optional "Add company details"
 * disclosure (Company name · Tax ID). Address line 2 is no longer collected; the saved value
 * is carried over untouched. A saved address renders as a two-column summary.
 */
export const BillingDetailsForm = forwardRef<BillingDetailsFormHandle, BillingDetailsFormProps>((
  { accountId, summary, editingSaved },
  ref,
) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const user = useSelector(fromAuth.selectUser);
  const account = useSelector(fromAccounts.selectAccountById(Number(accountId)));
  const currentBillingInformation = useSelector(
    fromBillingInformation.selectOneByAccountId(Number(accountId)),
  );
  const isNotSynced = useSelector(
    fromBillingInformation.isNotSyncedAddressByAccountId(Number(accountId)),
  ) ?? false;
  const defaultEmail = user?.email ?? '';
  // Out of sync with Recurly, or missing a required field: the address must be (re)confirmed.
  const needsReview = isNotSynced
    || (!!currentBillingInformation
      && !isBillingInformationComplete(currentBillingInformation, defaultEmail));

  const [companyOpen, setCompanyOpen] = useState(false);

  const schema = billingSchema(id => intl.formatMessage({ id }));

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    formState: { errors, isDirty: formDirty },
  } = useForm<BillingFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: toFormValues(currentBillingInformation, defaultEmail),
    resolver: yupResolver(schema),
  });

  // Saved address arriving (mount-time GET) or cancel of an edit → form mirrors the store again.
  useEffect(() => {
    reset(toFormValues(currentBillingInformation, defaultEmail));
    setCompanyOpen(
      !!(currentBillingInformation?.fullBusinessName || currentBillingInformation?.vatNumber),
    );
  }, [currentBillingInformation, defaultEmail, reset, summary]);

  const countryCode = watch('country');
  const countries = Country.getAllCountries();
  const selectedCountry = countries.find(it => it.isoCode === countryCode) ?? null;
  const states = selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode) : [];
  const hasStates = states.length > 0;
  const stateValue = watch('state');
  const selectedState = states
    .find(it => it.isoCode === stateValue || it.name === stateValue) ?? null;

  const hasChanges = (values: BillingFormValues) => !currentBillingInformation
    || needsReview
    || EDITABLE_KEYS.some(key => (currentBillingInformation[key] ?? '') !== (values[key] ?? ''));

  // The written record, or undefined when saving failed.
  const save = async (
    values: BillingFormValues,
  ): Promise<Partial<BillingInformation> | undefined> => {
    const billingInformation = {
      accountId: Number(accountId),
      ...(currentBillingInformation?.id ? { id: currentBillingInformation.id } : {}),
      ccEmail: values.ccEmail.trim(),
      country: values.country,
      address1: values.address1.trim(),
      city: values.city.trim(),
      state: values.state.trim(),
      postalCode: values.postalCode.trim(),
      fullBusinessName: values.fullBusinessName.trim(),
      vatNumber: values.vatNumber.trim(),
      // Not collected any more; keep whatever the account already had.
      address2: currentBillingInformation?.address2 ?? '',
    };
    try {
      await dispatch(fromBillingInformation.doCreateBillingInformation(billingInformation))
        .then(unwrapResult);
      track('Billing Information Saved', {
        id: user?.id,
        user_id: user?.email,
        email: user?.email,
        org_id: account?.id,
        org_name: account?.name,
      });
      return billingInformation;
    } catch (e: any) {
      if (e?.message === BILLING_ADDRESS_TAX_UNRESOLVABLE) {
        setError(
          'postalCode',
          { type: 'server', message: intl.formatMessage({ id: BILLING_ADDRESS_TAX_UNRESOLVABLE }) },
          { shouldFocus: true },
        );
      }
      return undefined;
    }
  };

  // Saves the form values when they differ from the stored billing information.
  const saveIfChanged = async (values: BillingFormValues): Promise<BillingSaveOutcome> => {
    if (!hasChanges(values)) {
      return { ok: true, saved: false };
    }
    const billingInformation = await save(values);
    return billingInformation
      ? { ok: true, saved: true, billingInformation }
      : { ok: false, saved: false };
  };

  useImperativeHandle(ref, () => ({
    validateAndSave: () => {
      if (summary && !needsReview) {
        return Promise.resolve({ ok: true, saved: false });
      }
      return new Promise<BillingSaveOutcome>(resolve => {
        const failed = () => resolve({ ok: false, saved: false });
        handleSubmit(
          values => saveIfChanged(values).then(resolve).catch(failed),
          failed,
        )().catch(failed);
      });
    },
    // "Unsaved changes" means the customer typed something since the form opened — not the
    // prefills the form adds on its own (the signed-in email when the saved address has none).
    isDirty: () => !summary && formDirty,
    discard: () => {
      reset(toFormValues(currentBillingInformation, defaultEmail));
      setCompanyOpen(
        !!(currentBillingInformation?.fullBusinessName || currentBillingInformation?.vatNumber),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    summary, needsReview, handleSubmit, formDirty, reset, currentBillingInformation, defaultEmail,
  ]);

  const renderLabel = (id: string, required: boolean, htmlFor: string) => (
    <Typography component="label" htmlFor={htmlFor} className={classes.label}>
      <FormattedMessage id={id} />
      {required && <Box component="span" aria-hidden="true" className={classes.required}> *</Box>}
    </Typography>
  );

  const renderError = (name: EditableKey) => (errors[name]?.message ? (
    <Typography id={`billing-${name}-error`} className={classes.errorText} role="alert">
      {errors[name]?.message}
    </Typography>
  ) : null);

  const renderTextField = (
    name: EditableKey,
    labelId: string,
    placeholderId: string,
    required: boolean,
    extra: { type?: string; autoComplete?: string } = {},
  ) => (
    <>
      {renderLabel(labelId, required, `billing-${name}`)}
      <Controller
        name={name}
        control={control}
        // The form's ref goes on the <input>, not InputBase's wrapper, so an error can focus it.
        render={({ field: { ref, ...field } }) => (
          <InputBase
            {...field}
            inputRef={ref}
            id={`billing-${name}`}
            className={`${classes.input} ${errors[name] ? classes.inputError : ''}`}
            placeholder={intl.formatMessage({ id: placeholderId })}
            type={extra.type}
            inputProps={{
              autoComplete: extra.autoComplete,
              'aria-invalid': !!errors[name],
              'aria-describedby': errors[name] ? `billing-${name}-error` : undefined,
            }}
          />
        )}
      />
      {renderError(name)}
    </>
  );

  if (summary && currentBillingInformation && !needsReview) {
    const billing = currentBillingInformation;
    const hasCompany = !!(billing.fullBusinessName || billing.vatNumber);
    const item = (labelId: string, value: string | undefined, full = false) => (
      <Box component="div" className={full ? classes.fullRow : classes.field}>
        <Typography component="dt" className={classes.summaryTerm}>
          <FormattedMessage id={labelId} />
        </Typography>
        <Typography component="dd" className={classes.summaryValue}>{value || '—'}</Typography>
      </Box>
    );
    return (
      <Box component="dl" id="billing-details-summary" className={classes.summary}>
        {item('page.order.confirmation.v2.billing.email', billing.ccEmail)}
        {item('page.order.confirmation.v2.billing.country', billing.country ? countryName(billing.country) : undefined)}
        {item('page.order.confirmation.v2.billing.address', billing.address1, true)}
        {item('page.order.confirmation.v2.billing.city', billing.city)}
        {item(
          'page.order.confirmation.v2.billing.state',
          billing.state && billing.country
            ? stateName(billing.country, billing.state)
            : billing.state,
        )}
        {item('page.order.confirmation.v2.billing.postal', billing.postalCode)}
        {hasCompany && item('page.order.confirmation.v2.billing.company.name', billing.fullBusinessName)}
        {hasCompany && item('page.order.confirmation.v2.billing.company.tax', billing.vatNumber)}
      </Box>
    );
  }

  return (
    <Box id="billing-details-form" sx={{ display: 'flex', flexDirection: 'column' }}>
      {editingSaved && (
        <Typography className={classes.editNote}>
          <FormattedMessage id="page.order.confirmation.v2.billing.edit.note" />
        </Typography>
      )}
      <Box className={classes.grid}>
        <Box className={classes.field}>
          {renderTextField(
            'ccEmail',
            'page.order.confirmation.v2.billing.email',
            'page.order.confirmation.v2.billing.email.placeholder',
            true,
            { type: 'email', autoComplete: 'email' },
          )}
        </Box>
        <Box className={classes.field}>
          {renderLabel('page.order.confirmation.v2.billing.country', true, 'country-select')}
          <Controller
            name="country"
            control={control}
            render={({ field }) => (
              <Box className={classes.select}>
                <CommonAutocomplete
                  id="country-select"
                  keyProp="country"
                  value={selectedCountry}
                  placeholder={intl.formatMessage({ id: 'page.order.confirmation.v2.billing.country.placeholder' })}
                  options={countries}
                  onChange={(_event, item) => {
                    field.onChange(item.isoCode);
                    setValue('state', '', { shouldValidate: false });
                  }}
                  inputKey="country"
                  errors={errors}
                  getOptionLabel={option => option.name}
                  disabled={false}
                  required={false}
                />
              </Box>
            )}
          />
        </Box>
        <Box className={classes.field}>
          {renderTextField(
            'address1',
            'page.order.confirmation.v2.billing.address',
            'page.order.confirmation.v2.billing.address.placeholder',
            true,
            { autoComplete: 'street-address' },
          )}
        </Box>
        <Box className={classes.field}>
          {renderTextField(
            'city',
            'page.order.confirmation.v2.billing.city',
            'page.order.confirmation.v2.billing.city.placeholder',
            true,
            { autoComplete: 'address-level2' },
          )}
        </Box>
        <Box className={classes.field}>
          {hasStates ? (
            <>
              {renderLabel('page.order.confirmation.v2.billing.state', true, 'state-select')}
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Box className={classes.select}>
                    <CommonAutocomplete
                      id="state-select"
                      keyProp="state"
                      value={selectedState}
                      placeholder={intl.formatMessage({ id: 'page.order.confirmation.v2.billing.state.select.placeholder' })}
                      options={states}
                      onChange={(_event, item) => field.onChange(item.name)}
                      inputKey="state"
                      errors={errors}
                      getOptionLabel={option => option.name}
                      disabled={false}
                      required={false}
                    />
                  </Box>
                )}
              />
            </>
          ) : renderTextField(
            'state',
            'page.order.confirmation.v2.billing.state',
            'page.order.confirmation.v2.billing.state.placeholder',
            true,
            { autoComplete: 'address-level1' },
          )}
        </Box>
        <Box className={classes.field}>
          {renderTextField(
            'postalCode',
            'page.order.confirmation.v2.billing.postal',
            'page.order.confirmation.v2.billing.postal.placeholder',
            true,
            { autoComplete: 'postal-code' },
          )}
        </Box>
      </Box>

      <button
        type="button"
        id="billing-company-toggle"
        className={classes.companyToggle}
        aria-expanded={companyOpen}
        onClick={() => setCompanyOpen(open => !open)}
      >
        <FormattedMessage
          id={companyOpen
            ? 'page.order.confirmation.v2.billing.company.toggle.close'
            : 'page.order.confirmation.v2.billing.company.toggle.open'}
        />
      </button>
      {companyOpen && (
        <Box id="billing-company-details" className={classes.companyPanel}>
          <Box className={classes.field}>
            {renderTextField(
              'fullBusinessName',
              'page.order.confirmation.v2.billing.company.name',
              'page.order.confirmation.v2.billing.company.name.placeholder',
              false,
              { autoComplete: 'organization' },
            )}
          </Box>
          <Box className={classes.field}>
            {renderTextField(
              'vatNumber',
              'page.order.confirmation.v2.billing.company.tax',
              'page.order.confirmation.v2.billing.company.tax.placeholder',
              false,
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
});
